import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2, RendererStyleFlags2 } from '@angular/core';

/**
 * Single owner of the application body zoom.
 *
 * The shell applies a compact `0.8` zoom on ≥1024px viewports and `1` otherwise, and mirrors
 * the value into the `--rt-body-zoom` CSS custom property on `<html>` (default `1` in
 * styles.css `:root`) so viewport-height rules — `vh` ignores zoom — can divide by it.
 * Centralising both writes here keeps the zoom and the token from ever drifting, and gives
 * features that need a fixed zoom (e.g. the BoldBI embed, which renders wrong under a body
 * zoom) a safe override: while an override is active the responsive re-evaluation no-ops,
 * and clearing it re-applies the zoom for the *current* viewport rather than a stale
 * captured value.
 */
@Injectable({ providedIn: 'root' })
export class BodyZoomService {

  /** Media query that decides whether the compact 0.8 body zoom applies (≥1024px viewports). */
  private readonly zoomMediaQuery: MediaQueryList;

  /** Renderer created via the factory — services have no host renderer to inject directly. */
  private readonly renderer: Renderer2;

  /** Fixed zoom demanded by an active override, or null when responsive zoom is in charge. */
  private overrideZoom: string | null = null;

  /** Guard so the media-query listener is attached exactly once. */
  private watching = false;

  /**
   * @param rendererFactory - Factory used to obtain a renderer for the body/html style writes.
   * @param document - Injected document; also the platform-safe route to `window.matchMedia`.
   */
  constructor(rendererFactory: RendererFactory2, @Inject(DOCUMENT) private document: Document) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.zoomMediaQuery = this.document.defaultView!.matchMedia('(min-width: 1024px)');
  }

  /**
   * Applies the zoom for the current viewport and starts re-evaluating it on every crossing
   * of the 1024px media-query band (window resized, snapped, moved to another monitor, or
   * the display resolution/scaling changes). Called once from the app shell's `ngOnInit`;
   * subsequent calls only re-apply the current value.
   * @returns Nothing.
   */
  init(): void {
    this.applyResponsiveZoom();
    if (!this.watching) {
      this.watching = true;
      this.zoomMediaQuery.addEventListener('change', () => this.applyResponsiveZoom());
    }
  }

  /**
   * Applies the viewport-dependent body zoom (`0.8` on ≥1024px viewports, `1` otherwise).
   * No-ops while an override is active so a media-query flip cannot stomp an overridden zoom.
   * @returns Nothing.
   */
  applyResponsiveZoom(): void {
    if (this.overrideZoom !== null) {
      return;
    }
    this.setBodyZoom(this.zoomMediaQuery.matches ? '0.8' : '1');
  }

  /**
   * Forces a fixed body zoom until {@link clearOverride} is called, suspending responsive
   * re-evaluation for the duration.
   * @param zoom - Zoom factor as a CSS value, e.g. `'1'`.
   * @returns Nothing.
   */
  overrideBodyZoom(zoom: string): void {
    this.overrideZoom = zoom;
    this.setBodyZoom(zoom);
  }

  /**
   * Ends an active override and re-applies the responsive zoom for the current viewport —
   * deliberately not a captured pre-override value, which would be stale if the viewport
   * crossed the media-query band while the override was active.
   * @returns Nothing.
   */
  clearOverride(): void {
    this.overrideZoom = null;
    this.applyResponsiveZoom();
  }

  /**
   * Applies a body zoom and mirrors it into the `--rt-body-zoom` CSS custom property on
   * `<html>` in one place, so the two can never drift.
   *
   * `RendererStyleFlags2.DashCase` is required for the token: it routes Renderer2 to
   * `style.setProperty`, the only path that writes a CSS custom property (plain `setStyle`
   * would assign `el.style['--rt-body-zoom']`, a silent no-op).
   * @param zoom - Zoom factor as a CSS value, e.g. `'0.8'`.
   * @returns Nothing.
   */
  private setBodyZoom(zoom: string): void {
    this.renderer.setStyle(this.document.body, 'zoom', zoom);
    this.renderer.setStyle(this.document.documentElement, '--rt-body-zoom', zoom, RendererStyleFlags2.DashCase);
  }
}
